import { useEffect } from 'react';
import { ChevronDown, Pause, Play, RotateCcw, RotateCw, Volume2, X, Sparkles } from 'lucide-react';
import BreathingCircle from '@/components/BreathingCircle';
import { usePlayer, formatTime } from '@/contexts/PlayerContext';
import { COLOR_META } from '@/lib/colorNoise';

const SPEEDS = [0.75, 1, 1.25, 1.5];
const BG_COLOR_OPTIONS = ['', 'pink', 'brown', 'white', 'green', 'blue', 'violet', 'grey'];
const BG_NATURE_OPTIONS = [
    { v: '', label: 'None' },
    { v: 'rain', label: 'Rain' },
    { v: 'waves', label: 'Ocean' },
    { v: 'forest', label: 'Forest' },
];

const PRE_MOODS = [
    { v: 'calm', label: 'Calm', emoji: '🌿' },
    { v: 'anxious', label: 'Anxious', emoji: '🌧' },
    { v: 'tired', label: 'Tired', emoji: '🌙' },
    { v: 'energized', label: 'Energized', emoji: '☀️' },
    { v: 'sad', label: 'Sad', emoji: '🫧' },
    { v: 'grateful', label: 'Grateful', emoji: '🍃' },
    { v: 'stressed', label: 'Stressed', emoji: '🌫' },
    { v: 'neutral', label: 'Neutral', emoji: '◯' },
];

export default function FullScreenPlayer() {
    const {
        track, fullscreen, setFullscreen, playing, toggle, progress, duration,
        seek, skip, volume, setVolume, speed, setSpeed, stop,
        preMoodGate, confirmPreMood,
        backgroundSound, bgVolume, bgEnabled, setBgVolume, setBgEnabled, overrideBackgroundSound,
    } = usePlayer();

    useEffect(() => {
        if (!fullscreen) return;
        const onEsc = (e) => { if (e.key === 'Escape') setFullscreen(false); };
        document.addEventListener('keydown', onEsc);
        return () => document.removeEventListener('keydown', onEsc);
    }, [fullscreen, setFullscreen]);

    if (!fullscreen || !track) return null;

    return (
        <div className="fixed inset-0 z-50 animate-fade-in" data-testid="fullscreen-player">
            <div
                className="absolute inset-0"
                style={{
                    backgroundImage: track.image
                        ? `linear-gradient(180deg, rgba(250,247,242,0.85), rgba(168,197,176,0.55)), url(${track.image})`
                        : 'linear-gradient(160deg, #FAF7F2, #C9DDCD 60%, #C8B6DB)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            />
            <div className="relative h-full w-full flex flex-col px-6 py-6 max-w-2xl mx-auto">
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => setFullscreen(false)}
                        aria-label="Minimize"
                        data-testid="fullscreen-minimize-button"
                        className="rounded-full w-10 h-10 grid place-items-center bg-white/60 hover:bg-white text-forest"
                    >
                        <ChevronDown size={20} />
                    </button>
                    <button
                        onClick={stop}
                        aria-label="Close"
                        data-testid="fullscreen-close-button"
                        className="rounded-full w-10 h-10 grid place-items-center bg-white/60 hover:bg-white text-forest"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="flex-1 grid place-items-center">
                    <div className="text-center">
                        <BreathingCircle size={260} label={playing ? 'Inhale … Exhale' : 'Paused'} />
                        <div className="mt-8">
                            <div className="text-xs uppercase tracking-[0.2em] text-forest-soft" data-testid="fullscreen-track-kind">
                                {track.kind?.replace('_', ' ') || 'Meditation'}
                            </div>
                            <h2 className="font-serif text-3xl text-forest mt-1" data-testid="fullscreen-track-title">{track.title}</h2>
                            {track.subtitle && <p className="text-forest-soft mt-1">{track.subtitle}</p>}
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    {preMoodGate ? (
                        <div className="qm-glass rounded-3xl p-6 border-white/60 animate-fade-in" data-testid="pre-mood-gate">
                            <p className="text-xs uppercase tracking-[0.25em] text-forest-soft mb-1">Before we begin</p>
                            <h3 className="font-serif text-2xl text-forest mb-3">How are you arriving?</h3>
                            <div className="flex flex-wrap gap-2 mb-4">
                                {PRE_MOODS.map((m) => (
                                    <button
                                        key={m.v}
                                        type="button"
                                        onClick={() => confirmPreMood(m.v)}
                                        data-testid={`pre-mood-${m.v}`}
                                        className="qm-pill border-forest/20 hover:bg-sage/30 bg-white/60"
                                    >
                                        <span className="mr-1">{m.emoji}</span>{m.label}
                                    </button>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={() => confirmPreMood(null)}
                                data-testid="pre-mood-skip"
                                className="text-sm text-forest-soft hover:text-forest underline"
                            >
                                Skip — just begin
                            </button>
                        </div>
                    ) : (
                    <>
                    <div>
                        <input
                            type="range"
                            min={0}
                            max={Math.max(duration || 0, 0.1)}
                            step={0.1}
                            value={progress || 0}
                            onChange={(e) => seek(Number(e.target.value))}
                            className="qm-range w-full"
                            data-testid="fullscreen-scrub"
                            aria-label="Seek"
                        />
                        <div className="flex justify-between text-xs text-forest-soft mt-1 tabular-nums">
                            <span>{formatTime(progress)}</span>
                            <span>{formatTime(duration)}</span>
                        </div>
                    </div>

                    <div className="flex items-center justify-center gap-4">
                        <button
                            onClick={() => skip(-15)}
                            aria-label="Back 15 seconds"
                            data-testid="fullscreen-skip-back"
                            className="rounded-full w-12 h-12 grid place-items-center text-forest hover:bg-white/60"
                        >
                            <RotateCcw size={22} />
                        </button>
                        <button
                            onClick={toggle}
                            aria-label={playing ? 'Pause' : 'Play'}
                            data-testid="fullscreen-toggle"
                            className="rounded-full w-16 h-16 grid place-items-center bg-forest text-cream hover:scale-105 active:scale-95 transition shadow-lg"
                        >
                            {playing ? <Pause size={26} /> : <Play size={26} className="ml-1" />}
                        </button>
                        <button
                            onClick={() => skip(15)}
                            aria-label="Forward 15 seconds"
                            data-testid="fullscreen-skip-forward"
                            className="rounded-full w-12 h-12 grid place-items-center text-forest hover:bg-white/60"
                        >
                            <RotateCw size={22} />
                        </button>
                    </div>

                    <div className="flex items-center justify-between gap-4 text-forest">
                        <div className="flex items-center gap-2 flex-1 max-w-[220px]">
                            <Volume2 size={18} />
                            <input
                                type="range" min={0} max={1} step={0.01} value={volume}
                                onChange={(e) => setVolume(Number(e.target.value))}
                                className="qm-range flex-1" aria-label="Volume"
                                data-testid="fullscreen-volume"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            {SPEEDS.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setSpeed(s)}
                                    data-testid={`speed-${s}`}
                                    className={`text-xs rounded-full px-3 py-1 border transition ${
                                        speed === s ? 'bg-forest text-cream border-forest' : 'border-forest/30 hover:bg-white/50'
                                    }`}
                                >
                                    {s}x
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Background sound controls — visible when an AI session
                        has an attached background. Users can turn it off,
                        swap the colour/nature, or ride the volume. */}
                    {backgroundSound && (
                        <div className="qm-glass rounded-3xl p-4 border-white/60" data-testid="fs-bg-panel">
                            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                                <div className="flex items-center gap-2 text-forest">
                                    <Sparkles size={14} className="text-forest-soft" />
                                    <div className="text-xs uppercase tracking-[0.2em] text-forest-soft">Background sound</div>
                                </div>
                                <label className="flex items-center gap-2 text-xs text-forest cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={bgEnabled}
                                        onChange={(e) => setBgEnabled(e.target.checked)}
                                        data-testid="fs-bg-enabled"
                                    />
                                    {bgEnabled ? 'On · ducks under voice' : 'Off'}
                                </label>
                            </div>
                            {bgEnabled && (
                                <>
                                    <div className="flex items-center gap-2 mb-3">
                                        <Volume2 size={14} className="text-forest-soft" />
                                        <input
                                            type="range" min={0} max={1} step={0.02} value={bgVolume}
                                            onChange={(e) => setBgVolume(Number(e.target.value))}
                                            className="qm-range flex-1" aria-label="Background volume"
                                            data-testid="fs-bg-volume"
                                        />
                                        <span className="text-xs text-forest-soft w-10 text-right tabular-nums">{Math.round(bgVolume * 100)}%</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                        {BG_COLOR_OPTIONS.map((c) => (
                                            <button
                                                key={c || 'none'}
                                                type="button"
                                                onClick={() => overrideBackgroundSound({ ...backgroundSound, color: c || null })}
                                                data-testid={`fs-bg-color-${c || 'none'}`}
                                                aria-pressed={(backgroundSound.color || '') === c}
                                                className={`text-[11px] rounded-full px-2.5 py-1 border transition ${
                                                    (backgroundSound.color || '') === c ? 'bg-forest text-cream border-forest' : 'border-forest/30 hover:bg-white/50'
                                                }`}
                                            >
                                                {c ? COLOR_META[c].label : 'No colour'}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {BG_NATURE_OPTIONS.map((n) => (
                                            <button
                                                key={n.v || 'none'}
                                                type="button"
                                                onClick={() => overrideBackgroundSound({ ...backgroundSound, nature: n.v || null })}
                                                data-testid={`fs-bg-nature-${n.v || 'none'}`}
                                                aria-pressed={(backgroundSound.nature || '') === n.v}
                                                className={`text-[11px] rounded-full px-2.5 py-1 border transition ${
                                                    (backgroundSound.nature || '') === n.v ? 'bg-forest text-cream border-forest' : 'border-forest/30 hover:bg-white/50'
                                                }`}
                                            >
                                                {n.label}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    </>
                    )}
                </div>
            </div>
        </div>
    );
}
